import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { UploadCloud } from 'lucide-react'
import { Button, Checkbox, Chip, Drawer, Radio } from '@/components/ui'
import { ApiError } from '@/lib/api/jsonFetch'
import { useForecastImportCommit, useForecastImportPreview } from '../api/queries'
import type {
  DateOrder,
  ImportChoices,
  ImportMatchSource,
  ImportPreview,
  ImportRow,
} from '../api/types'
import { PricelistAwareProductPicker } from './PricelistAwareProductPicker'

export interface ForecastImportDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  customerId: number
}

const ACCEPT = '.xlsx,.XLSX,.csv,.CSV,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv'

/** "2027-03-02" → "Mar 2, 2027". Month spelled out so a day/month swap is
 *  visible at a glance; UTC so the date never shifts by timezone. */
function formatImportDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatPeriod(start: string | null, end: string | null): string {
  if (!start) return '—'
  return start === end || !end ? formatImportDate(start) : `${formatImportDate(start)} – ${formatImportDate(end)}`
}

function errorText(err: unknown): string {
  if (err instanceof ApiError && typeof err.detail === 'string') return err.detail
  return err instanceof Error ? err.message : String(err)
}

const MATCH_LABEL: Record<ImportMatchSource, string> = {
  part_number: 'Their part number',
  default_code: 'SPI code',
  override: 'Chosen',
  description: 'Suggested by description',
  other_pricelist: 'Suggested from another pricelist',
  unmatched: 'No match',
}

function qtyText(q: string | null): string {
  if (q === null) return '—'
  const n = Number(q)
  return Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 4 }) : q
}

/**
 * Import a customer's forecast spreadsheet (.xlsx / .csv). The server
 * parses and matches it (preview, no writes); the user confirms the date
 * order, product suggestions and skipped rows; commit re-runs the same
 * plan server-side and replaces the customer's overlapping lines.
 */
export function ForecastImportDrawer({ open, onOpenChange, customerId }: ForecastImportDrawerProps) {
  const fileRef = useRef<HTMLInputElement | null>(null)
  const preview = useForecastImportPreview()
  const commit = useForecastImportCommit()
  const seq = useRef(0)
  const [file, setFile] = useState<File | null>(null)
  const [dateOrder, setDateOrder] = useState<DateOrder | null>(null)
  const [overrides, setOverrides] = useState<Record<number, number>>({})
  const [skip, setSkip] = useState<number[]>([])
  const [result, setResult] = useState<ImportPreview | null>(null)
  const [pickingRow, setPickingRow] = useState<number | null>(null)

  function run(next: Partial<Omit<ImportChoices, 'customerId'>>) {
    const choices: ImportChoices = {
      file: next.file ?? file!,
      customerId,
      dateOrder: next.dateOrder !== undefined ? next.dateOrder : dateOrder,
      overrides: next.overrides ?? overrides,
      skip: next.skip ?? skip,
    }
    if (!choices.file) return
    const mine = ++seq.current
    preview.mutate(choices, {
      onSuccess: (res) => {
        if (mine !== seq.current) return
        setResult(res)
        if (choices.dateOrder === null && (res.date_order_detected === 'DMY' || res.date_order_detected === 'MDY')) {
          setDateOrder(res.date_order_detected)
        }
      },
    })
  }

  function onFile(f: File | undefined) {
    if (!f) return
    setFile(f)
    setDateOrder(null)
    setOverrides({})
    setSkip([])
    setResult(null)
    run({ file: f, dateOrder: null, overrides: {}, skip: [] })
  }

  function chooseOrder(order: DateOrder) {
    setDateOrder(order)
    run({ dateOrder: order })
  }

  /** Applies to every row with the same part number (or, with no part
   *  number, the same description) that wasn't matched outright — a
   *  sheet usually repeats one code across many dates. */
  function chooseProduct(rowNo: number, productId: number) {
    const picked = result?.rows.find((r) => r.row_no === rowNo)
    const sameKey = (r: ImportRow) =>
      picked?.code ? r.code === picked.code : !!picked?.description && r.description === picked.description
    const next = { ...overrides, [rowNo]: productId }
    for (const r of result?.rows ?? []) {
      if (r.row_no !== rowNo && sameKey(r) && r.match_source !== 'part_number' && r.match_source !== 'default_code') {
        next[r.row_no] = productId
      }
    }
    setOverrides(next)
    setPickingRow(null)
    run({ overrides: next })
  }

  function toggleSkip(rowNo: number) {
    const next = skip.includes(rowNo) ? skip.filter((n) => n !== rowNo) : [...skip, rowNo]
    setSkip(next)
    run({ skip: next })
  }

  function close() {
    seq.current++
    setFile(null)
    setDateOrder(null)
    setOverrides({})
    setSkip([])
    setResult(null)
    setPickingRow(null)
    preview.reset()
    commit.reset()
    onOpenChange(false)
  }

  function doImport() {
    if (!file || !result) return
    commit.mutate(
      { file, customerId, dateOrder, overrides, skip },
      {
        onSuccess: (res) => {
          toast.success(
            `Imported ${res.created} forecast line${res.created === 1 ? '' : 's'}` +
              (res.replaced ? `, replaced ${res.replaced}` : ''),
          )
          close()
        },
      },
    )
  }

  const hasTextDates = result ? result.date_order_detected !== 'none' : false
  const orderMissing = !!result?.needs_date_order && dateOrder === null
  const blocking = result ? result.rows.filter((r) => !r.skipped && !r.importable) : []
  const canImport = !!result && result.can_commit && !orderMissing && !preview.isPending && !commit.isPending

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      size="xl"
      title="Import forecast spreadsheet"
      meta="Upload the customer's forecast (.xlsx or .csv). Needs a product column (their part number or a description), a quantity and a date. Each row becomes a forecast line for that date; rows with the same product and date are added together."
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-xs text-secondary">
            {result
              ? orderMissing
                ? 'Choose the date order to continue.'
                : blocking.length
                  ? `${blocking.length} row${blocking.length === 1 ? '' : 's'} need a product or must be skipped.`
                  : `${result.lines_to_create.length} line${result.lines_to_create.length === 1 ? '' : 's'} to import` +
                    (result.lines_to_replace.length ? `, ${result.lines_to_replace.length} existing to replace` : '')
              : null}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={close}>
              Cancel
            </Button>
            {result ? (
              <Button variant="primary" size="sm" disabled={!canImport} onClick={doImport}>
                {commit.isPending ? 'Importing…' : `Import ${result.lines_to_create.length} lines`}
              </Button>
            ) : null}
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {!result ? (
          <div
            onDrop={(e) => {
              e.preventDefault()
              onFile(e.dataTransfer?.files?.[0])
            }}
            onDragOver={(e) => e.preventDefault()}
            className="flex flex-col items-center justify-center gap-2 rounded-sm border-2 border-dashed border-border-default bg-surface-sunk px-6 py-10 text-center"
          >
            <UploadCloud size={28} className="text-tertiary" />
            <p className="m-0 text-sm text-secondary">Drag &amp; drop the spreadsheet here</p>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              data-testid="forecast-import-file"
              onChange={(e) => {
                const f = e.target.files?.[0]
                e.target.value = ''
                onFile(f)
              }}
            />
            <Button variant="primary" size="sm" onClick={() => fileRef.current?.click()} disabled={preview.isPending}>
              {preview.isPending ? 'Reading…' : 'Choose file'}
            </Button>
            <p className="m-0 text-xs text-tertiary">.xlsx or .csv, up to 10 MB</p>
            {preview.isError ? <p className="m-0 text-xs text-danger">{errorText(preview.error)}</p> : null}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-role-data-primary">{result.filename}</span>
              {result.sheet_name && result.sheet_name !== result.filename ? (
                <Chip size="xs">Sheet: {result.sheet_name}</Chip>
              ) : null}
              <Chip size="xs">{result.rows.length} rows</Chip>
              <span className="text-xs text-tertiary">Columns used: {Object.values(result.columns).join(', ')}</span>
              <Button variant="ghost" size="sm" className="ml-auto" onClick={() => fileRef.current?.click()}>
                Choose another file
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  e.target.value = ''
                  onFile(f)
                }}
              />
            </div>

            {hasTextDates ? (
              <section
                className={
                  'flex flex-col gap-2 rounded-sm border p-3 ' +
                  (orderMissing ? 'border-warning-border bg-warning-bg' : 'border-border-default bg-surface')
                }
                aria-label="Date order"
              >
                <span className="text-role-section-label">How are the dates written?</span>
                <div className="flex flex-wrap gap-4">
                  <Radio
                    name="forecast-import-date-order"
                    label="Day / Month / Year (02/03/2027 = Mar 2, 2027)"
                    checked={dateOrder === 'DMY'}
                    onChange={() => chooseOrder('DMY')}
                  />
                  <Radio
                    name="forecast-import-date-order"
                    label="Month / Day / Year (02/03/2027 = Feb 3, 2027)"
                    checked={dateOrder === 'MDY'}
                    onChange={() => chooseOrder('MDY')}
                  />
                </div>
                <p className="m-0 text-xs text-secondary">
                  {result.date_order_detected === 'DMY' || result.date_order_detected === 'MDY'
                    ? `Detected from the file: some dates only make sense as ${
                        result.date_order_detected === 'DMY' ? 'day/month' : 'month/day'
                      }. Check the dates below.`
                    : result.date_order_detected === 'mixed'
                      ? 'The file mixes both orders. Pick one; rows that don’t fit are flagged.'
                      : 'Every date in this file fits both ways. Choose the order before importing.'}
                </p>
              </section>
            ) : null}

            {[...result.warnings, ...result.errors].map((w) => (
              <p key={w} className="m-0 text-xs text-warning-text">
                {w}
              </p>
            ))}

            <div className="overflow-x-auto rounded-sm border border-border-default">
              <table className="w-full border-collapse text-sm">
                <thead className="bg-surface-sunk text-left text-2xs uppercase text-tertiary">
                  <tr>
                    <th className="px-2 py-1.5">Row</th>
                    <th className="px-2 py-1.5">In the file</th>
                    <th className="px-2 py-1.5">SPI product</th>
                    <th className="px-2 py-1.5">Date</th>
                    <th className="px-2 py-1.5 text-right">Qty</th>
                    <th className="px-2 py-1.5">Skip</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((row) => (
                    <ImportRowView
                      key={row.row_no}
                      row={row}
                      customerId={customerId}
                      picking={pickingRow === row.row_no}
                      onPick={() => setPickingRow(row.row_no)}
                      onChoose={(pid) => chooseProduct(row.row_no, pid)}
                      onCancelPick={() => setPickingRow(null)}
                      onToggleSkip={() => toggleSkip(row.row_no)}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {result.lines_to_replace.length ? (
              <section className="flex flex-col gap-1" aria-label="Lines to replace">
                <span className="text-role-section-label">
                  Will replace {result.lines_to_replace.length} existing line
                  {result.lines_to_replace.length === 1 ? '' : 's'}
                </span>
                <p className="m-0 text-xs text-secondary">
                  For each product in the file, existing lines inside the file’s date range for that product are
                  deleted and replaced by the file’s lines. Other products and dates are left alone.
                </p>
                <ul className="m-0 flex list-none flex-col gap-0.5 p-0 text-xs text-secondary">
                  {result.lines_to_replace.map((l) => (
                    <li key={l.id}>
                      {l.product_name} · {formatPeriod(l.period_start, l.period_end)} · qty {qtyText(l.qty)}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {preview.isError ? <p className="m-0 text-xs text-danger">{errorText(preview.error)}</p> : null}
            {commit.isError ? <p className="m-0 text-xs text-danger">{errorText(commit.error)}</p> : null}
          </>
        )}
      </div>
    </Drawer>
  )
}

interface ImportRowViewProps {
  row: ImportRow
  customerId: number
  picking: boolean
  onPick: () => void
  onChoose: (productId: number) => void
  onCancelPick: () => void
  onToggleSkip: () => void
}

function ImportRowView({ row, customerId, picking, onPick, onChoose, onCancelPick, onToggleSkip }: ImportRowViewProps) {
  const suggestion = row.match_source === 'description' || row.match_source === 'other_pricelist'
  const variant = row.skipped ? 'neutral' : row.importable ? 'success' : suggestion ? 'warning' : 'danger'
  const others = row.candidates.filter((c) => c.id !== row.product?.id)
  return (
    <tr className={'border-t border-border-subtle align-top ' + (row.skipped ? 'opacity-50' : '')} data-testid={`import-row-${row.row_no}`}>
      <td className="px-2 py-1.5 text-tertiary">{row.row_no}</td>
      <td className="px-2 py-1.5">
        <div>{row.code ?? '—'}</div>
        <div className="text-xs text-secondary">{row.description}</div>
      </td>
      <td className="px-2 py-1.5">
        {picking ? (
          <div className="flex flex-col gap-1">
            <PricelistAwareProductPicker
              customerId={customerId}
              value={row.product ? [row.product.id, row.product.name] : null}
              onChange={(v) => (v ? onChoose(v[0]) : undefined)}
            />
            <button type="button" className="self-start text-2xs text-link hover:underline" onClick={onCancelPick}>
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-1.5">
              {row.product ? <span>{row.product.name}</span> : null}
              <Chip size="xs" variant={variant}>
                {MATCH_LABEL[row.match_source]}
              </Chip>
            </div>
            {row.info.map((m) => (
              <span key={m} className="text-xs text-secondary">
                {m}
              </span>
            ))}
            {!row.skipped ? (
              <div className="flex flex-wrap items-center gap-2 text-2xs">
                {suggestion && row.product ? (
                  <button type="button" className="text-link hover:underline" onClick={() => onChoose(row.product!.id)}>
                    Use this product
                  </button>
                ) : null}
                {others.map((c) => (
                  <button key={c.id} type="button" className="text-link hover:underline" onClick={() => onChoose(c.id)}>
                    Use {c.name}
                  </button>
                ))}
                <button type="button" className="text-link hover:underline" onClick={onPick}>
                  {row.product ? 'Pick another' : 'Pick product'}
                </button>
              </div>
            ) : null}
          </div>
        )}
        {row.errors.map((e) => (
          <div key={e} className="text-xs text-danger">
            {e}
          </div>
        ))}
      </td>
      <td className="px-2 py-1.5 whitespace-nowrap">
        {formatPeriod(row.period_start, row.period_end)}
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums">{qtyText(row.qty)}</td>
      <td className="px-2 py-1.5">
        <Checkbox aria-label={`Skip row ${row.row_no}`} checked={row.skipped} onChange={onToggleSkip} />
      </td>
    </tr>
  )
}
